// Leaderboards — May 29, 2026.
// Top earners + top referrers + top silo owners. Live snapshot.
import { useEffect, useState } from "react";
import { Trophy, Spinner, Sparkle, Users, Coins } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmt$ = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LeaderboardsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/leaderboards").then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="leaderboards-loading">
        <Spinner size={14} className="animate-spin" /> Loading leaderboards…
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-[#6B6A68]">No data yet. Run some test seeds first.</div>;
  }

  return (
    <div className="space-y-8" data-testid="leaderboards-tab">
      <div>
        <div className="flex items-baseline gap-3 mb-1">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">🏆 Leaderboards</h2>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">live snapshot</span>
        </div>
        <p className="text-xs text-[#6B6A68]">Top earners + top referrers + top silo owners. Refreshes every time you click the tab.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Top earners" icon={Coins} accent="#2F6B3A" rows={data.top_earners}
          renderRow={(r, i) => <Row i={i} primary={r.email} secondary={`${r.payout_count} payouts · ${r.affiliate_count} referrals`} amount={fmt$(r.total_earned_cents)} />}
          empty="No earnings yet. Run Option 4 to populate." testid="lb-earners" />
        <Card title="Top referrers" icon={Users} accent="#4A5D4E" rows={data.top_referrers}
          renderRow={(r, i) => <Row i={i} primary={r.email} secondary={`tier: ${r.affiliate_tier || 'newcomer'}`} amount={`${r.referral_count}`} amountLabel="refs" />}
          empty="No referrals yet." testid="lb-referrers" />
        <Card title="Top silo builders" icon={Sparkle} accent="#7B7AA8" rows={data.top_silo_owners}
          renderRow={(r, i) => <Row i={i} primary={r.email} secondary={`${r.doc_count} docs`} amount={`${r.silo_count}`} amountLabel="silos" />}
          empty="No silos yet." testid="lb-silos" />
      </div>

      {/* Downline tree for top earner */}
      {data.top_earner_tree && data.top_earner_tree.downline?.length > 0 && (
        <div className="bg-white border hairline rounded-lg p-5" data-testid="lb-downline-tree">
          <div className="flex items-baseline gap-2 mb-3">
            <Trophy size={16} weight="duotone" className="text-[#C5A572]" />
            <h3 className="font-serif-display text-lg">Top referrer's network</h3>
            <span className="text-xs text-[#6B6A68]">{data.top_earner_tree.affiliate_email}</span>
          </div>
          <div className="space-y-1">
            {data.top_earner_tree.downline.map((d) => (
              <div key={d.email} className="text-xs flex items-center gap-2 py-1 border-b border-[#E5E3DE] last:border-0" data-testid={`downline-${d.email}`}>
                <span className="text-[#6B6A68] tabular-nums w-8 text-right">{d.role === 'affiliate' ? '🎯' : d.role === 'creator' ? '👤' : '🌱'}</span>
                <span className="font-mono text-[10px] text-[#1C1B1A] flex-1 truncate">{d.email}</span>
                <span className="text-[10px] text-[#6B6A68]">{d.role === 'affiliate' ? 'Creator Pro' : d.role}</span>
                <span className="text-[#2F6B3A] tabular-nums text-xs">{fmt$(d.contributed_cents)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-[#6B6A68] italic">
        Source: `affiliate_payouts` + `referrals` + `silos` collections. Test seeds (@mysilo-test, @random-test, @stacked-test) count too — flip the toggle in the API call later when going live.
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, accent, rows, renderRow, empty, testid }) {
  return (
    <div className="bg-white border hairline rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="duotone" style={{ color: accent }} />
        <div className="text-xs uppercase tracking-[0.18em] text-[#1C1B1A]">{title}</div>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="text-[11px] text-[#6B6A68] italic py-3 text-center">{empty}</div>
      ) : (
        <div className="space-y-1">
          {rows.slice(0, 10).map((r, i) => renderRow(r, i))}
        </div>
      )}
    </div>
  );
}

function Row({ i, primary, secondary, amount, amountLabel }) {
  const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
  return (
    <div className="flex items-center gap-2 py-1 border-b border-[#E5E3DE] last:border-0 text-[11px]">
      <span className="w-6 text-center">{medal}</span>
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[10px] text-[#1C1B1A] truncate">{primary}</div>
        <div className="text-[9px] text-[#6B6A68]">{secondary}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="tabular-nums text-[#1C1B1A]">{amount}</div>
        {amountLabel && <div className="text-[9px] text-[#6B6A68]">{amountLabel}</div>}
      </div>
    </div>
  );
}
